import { getAnonymousId } from '../utils/anonymousId';
import { getClickIds, getUtmsForProfile } from './utm-manager';
import { AFFILIATE_CLICK_ID_TYPE, readAffiliateClick } from './affiliate';
import type { UpdateUserProfilePayload } from '../services/users.service';

/** Canal de adquisición con el que el alta por OTP se estampa desde siempre.
 *
 * OJO al leer reportes: en el catálogo del backend el 5 es **'Invoicing
 * platform'**, no 'web'. Es un valor heredado que ya no describe lo que pasa,
 * pero cambiarlo reetiquetaría el histórico y es decisión de datos, no de este
 * módulo. Se reproduce tal cual para no introducir una segunda mentira.
 *
 * Lo consumen los DOS caminos de alta: el PATCH de perfil (OTP) y el body de
 * `/auth/google/login` (Google). Si solo uno lo manda, el otro nace sin canal:
 * así quedaron 12k altas con Google entre jun y sep de 2026. */
export const WEBAPP_ACQUISITION_CHANNEL_ID = 5;

/** Publisher del afiliado (`sub2`) dentro de `utms_extra`. */
const AFFILIATE_PID_KEY = 'affise_pid';

/**
 * El parche de adquisición que se manda al COMPLETAR el alta: de dónde vino el
 * usuario, estampado en su perfil.
 *
 * Vive aquí y no en cada pantalla de auth porque los dos caminos de alta —OTP y
 * Google— tienen que mandar EXACTAMENTE lo mismo. Cuando divergen, la
 * atribución de un usuario depende de con qué botón se registró, que es la
 * clase de diferencia que nadie nota hasta que no cuadra un reporte.
 *
 * El clic de afiliado va en `utms_extra` ADEMÁS de viajar en el touchpoint, y
 * la redundancia es deliberada. El postback que factura es el del mes 2: se
 * cobra server-side, meses después, sin navegador. Ese día el backend necesita
 * el clickid de este usuario — por touchpoint lo alcanza con un join contra
 * `anonymous_id`, en el perfil lo tiene directo. Siendo la llave con la que se
 * cobra una comisión, se guarda por la ruta más corta.
 */
/**
 * Los click IDs que el navegador trae guardados, con el nombre EXACTO que el
 * backend espera en `utms_extra`.
 *
 * Es su propia función porque tiene dos consumidores que mandan a endpoints
 * distintos: el PATCH de `update-user` (alta por OTP) y el body de
 * `/auth/google/login` (alta con Google). Si las llaves divergieran entre los
 * dos, el mismo clic quedaría guardado con dos nombres y la lista blanca del
 * stitch de UME solo reconocería uno.
 */
export const buildUtmsExtra = (): Record<string, string> => {
	const affiliate = readAffiliateClick();
	return {
		...getClickIds(),
		...(affiliate && {
			[AFFILIATE_CLICK_ID_TYPE]: affiliate.clickId,
			...(affiliate.pid && { [AFFILIATE_PID_KEY]: affiliate.pid }),
		}),
	};
};

export const buildAcquisitionProfilePatch = (): UpdateUserProfilePayload => {
	const utmsExtra = buildUtmsExtra();

	return {
		acquisition_channel_id: WEBAPP_ACQUISITION_CHANNEL_ID,
		anonymous_id: getAnonymousId(),
		...getUtmsForProfile(),
		...(Object.keys(utmsExtra).length > 0 && { utms_extra: utmsExtra }),
	};
};
